// Marketplace storefront.
//
// Reads `marketplace_public`, the view that deliberately omits asset_url — a
// listing row has to be readable to render at all, so selecting the download
// link through the table would hand every browser the file for free.
//
// Layout note: the grid is auto-fit rather than a fixed column count, and the
// first card in a populated grid is allowed to span two columns. A wall of
// identically-sized cards is the most generic possible shape for this page, and
// one break in the rhythm is enough to stop it reading as a template.

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Clock, Eye, Loader, Package, ShoppingBag } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { supabase } from '../config/supabase';
import { gathrxrAuthHeader } from '../utils/quotaHeaders';
import CheckoutModal from '../components/checkout/CheckoutModal';
import type { EmbeddedSession } from '../services/checkout';
import ListingSheet from '../components/marketplace/ListingSheet';
import { startListingCheckout as startCheckoutFor } from '../components/marketplace/buyListing';
import {
  MARKETPLACE_PURCHASES_ENABLED,
  MARKETPLACE_COMING_SOON_MESSAGE,
} from '../config/marketplace';
import './Marketplace.css';

interface Listing {
  id: string;
  creator_id: string;
  title: string;
  description: string | null;
  kind: string;
  price_cents: number;
  purchasable?: boolean;
  sale_type?: 'copy' | 'original';
  currency: string;
  thumbnail_url: string | null;
  sales_count: number;
}

// Shared across the marketplace surfaces so the nav does not drift between
// them — the failure mode is a link set that is subtly different on each page.
export const MARKET_LINKS = [
  { label: 'Browse', to: '/marketplace' },
  { label: 'Your listings', to: '/creator/listings' },
  { label: 'Payouts', to: '/creator/payouts' },
  { label: 'Dashboard', to: '/dashboard' },
];

/** Where a bought item lands, by listing kind (mirrors deliveryPlace on the server). */
const placeFor = (kind?: string) =>
  kind === 'world' ? { label: 'your worlds', to: '/dashboard' }
  : kind === 'avatar' ? { label: 'your avatars', to: '/avatars' }
  : kind ? { label: 'My Uploads (open any world, then My Uploads)', to: '/dashboard' }
  : { label: 'your worlds, avatars or My Uploads', to: '/dashboard' };

type ReturnState =
  | { kind: 'waiting'; listingId: string }
  | { kind: 'done'; listingId: string; itemKind?: string }
  | { kind: 'slow'; listingId: string }
  | { kind: 'canceled' };

const money = (cents: number, currency = 'usd') =>
  new Intl.NumberFormat(undefined, { style: 'currency', currency: currency.toUpperCase() })
    .format(cents / 100);

export default function Marketplace() {
  const navigate = useNavigate();
  const [listings, setListings] = useState<Listing[]>([]);
  const [owned, setOwned] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState<string | null>(null);
  // The listing whose in-site checkout sheet is open.
  const [paying, setPaying] = useState<Listing | null>(null);
  const [error, setError] = useState('');
  const [params, setParams] = useSearchParams();
  // Creator usernames, for "by @name" in the detail sheet.
  const [creators, setCreators] = useState<Record<string, string>>({});
  const [me, setMe] = useState<string | null>(null);
  // Detail sheet: /marketplace?item=<id> opens it, so a listing can be shared.
  const openItemId = params.get('item');
  const openItem = openItemId ? listings.find((l) => l.id === openItemId) || null : null;
  const setOpenItem = (id: string | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set('item', id); else next.delete('item');
    setParams(next, { replace: !id });
  };
  const tryIt = (l: Listing) => navigate(`/marketplace/preview/${l.id}`);
  // Coming back from Stripe Checkout. The redirect proves nothing — the
  // webhook is what delivers — so the page waits for the entitlement to appear
  // rather than announcing success off a URL anyone can type.
  const [ret, setRet] = useState<ReturnState | null>(() => {
    const bought = params.get('purchased');
    if (bought) return { kind: 'waiting', listingId: bought };
    if (params.get('canceled')) return { kind: 'canceled' };
    return null;
  });

  useEffect(() => {
    if (ret?.kind !== 'waiting') return;
    let stop = false;
    let tries = 0;
    const tick = async () => {
      if (stop) return;
      tries += 1;
      const { data } = await supabase
        .from('marketplace_entitlements')
        .select('listing_id')
        .eq('listing_id', ret.listingId)
        .is('revoked_at', null)
        .limit(1);
      if (stop) return;
      if (data?.length) {
        const itemKind = listings.find((l) => l.id === ret.listingId)?.kind;
        setRet({ kind: 'done', listingId: ret.listingId, itemKind });
        setOwned((o) => new Set(o).add(ret.listingId));
        load();
        return;
      }
      if (tries >= 20) { setRet({ kind: 'slow', listingId: ret.listingId }); return; }
      setTimeout(tick, 2000);
    };
    tick();
    return () => { stop = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ret?.kind]);

  const dismissReturn = () => {
    setRet(null);
    const next = new URLSearchParams(params);
    next.delete('purchased'); next.delete('session_id'); next.delete('canceled');
    setParams(next, { replace: true });
  };

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error: e } = await supabase
      .from('marketplace_public')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(60);
    if (e) setError(e.message);
    const rows = (data as Listing[]) || [];
    setListings(rows);
    const ids = [...new Set(rows.map((r) => r.creator_id))];
    if (ids.length) {
      const { data: profs } = await supabase.from('profiles').select('id, username').in('id', ids);
      setCreators(Object.fromEntries((profs || []).filter((p: any) => p.username).map((p: any) => [p.id, p.username])));
    }

    // What the viewer already owns, so bought items show as owned rather than
    // offering to sell them the same thing twice.
    const { data: { session } } = await supabase.auth.getSession();
    setMe(session?.user?.id ?? null);
    if (session) {
      const { data: ent } = await supabase
        .from('marketplace_entitlements')
        .select('listing_id')
        .is('revoked_at', null);
      setOwned(new Set((ent || []).map((r: any) => r.listing_id)));
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const buy = async (listing: Listing) => {
    // Purchases are switched off until checkout ships (the server refuses too).
    if (!MARKETPLACE_PURCHASES_ENABLED) {
      setError(MARKETPLACE_COMING_SOON_MESSAGE);
      return;
    }
    setError('');
    const headers = await gathrxrAuthHeader();
    if (!headers['x-gathrxr-token']) {
      setError('Sign in to buy.');
      return;
    }
    // The payment form opens inside GathrXR (Stripe Embedded Checkout).
    setPaying(listing);
  };

  /** Creates the listing's session; reservation and expiry live server-side. */
  const startListingCheckout = (listing: Listing) => async (): Promise<EmbeddedSession> => {
    setBuying(listing.id);
    try {
      return await startCheckoutFor(listing.id);
    } finally {
      setBuying(null);
    }
  };

  /** Stripe says the form is done; the webhook delivers, the page waits for it. */
  const listingPaid = (listing: Listing) => () => {
    setPaying(null);
    setRet({ kind: 'waiting', listingId: listing.id });
  };

  return (
    <div className="mk-page mk-page-hero">
      <PageHeader
        title="Marketplace"
        accent="Made by builders."
        size="tall"
        subtitle="Avatars, props, models and whole worlds, made and sold by people building on GathrXR. Creators keep 80% of every sale."
        hero="/hero-marketplace.webp"
        links={MARKET_LINKS}
        actions={
          <button className="mk-primary" onClick={() => navigate('/creator/listings')}>
            Sell your work
          </button>
        }
      />

      {!MARKETPLACE_PURCHASES_ENABLED && (
        // Wrapped: direct children of .mk-page-hero get the page gutter as
        // padding, which would otherwise land inside the banner itself.
        <div>
        <div className="mk-banner mk-banner-soon" role="status">
          <Clock size={17} />
          <div>
            <strong>Buying is coming soon</strong>
            <p>
              Browse what builders have listed. {MARKETPLACE_COMING_SOON_MESSAGE} Creators
              can already list their work so it's ready the day sales open.
            </p>
          </div>
        </div>
        </div>
      )}

      {ret && (
        <div>
        <div className={`mk-banner ${ret.kind === 'canceled' || ret.kind === 'waiting' ? 'mk-banner-soon' : 'mk-banner-ok'}`} role="status">
          {ret.kind === 'waiting' ? <Loader size={17} className="spin" /> : ret.kind === 'canceled' ? <Clock size={17} /> : <CheckCircle2 size={17} />}
          <div>
            {ret.kind === 'waiting' && (<><strong>Payment received, delivering your item…</strong><p>This usually takes a few seconds.</p></>)}
            {ret.kind === 'done' && (() => {
              const place = placeFor(listings.find((l) => l.id === ret.listingId)?.kind ?? ret.itemKind);
              return (<><strong>It's yours</strong><p>Find it in <a href={place.to} onClick={(e) => { e.preventDefault(); navigate(place.to); }}>{place.label}</a>. A receipt is on its way to your email.</p></>);
            })()}
            {ret.kind === 'slow' && (<><strong>Your payment went through</strong><p>Delivery is taking longer than usual. It will appear in your worlds, avatars or My Uploads shortly, and your receipt email says where. If it hasn't arrived in an hour, contact support@gathrxr.com.</p></>)}
            {ret.kind === 'canceled' && (<><strong>Checkout canceled</strong><p>You weren't charged.</p></>)}
          </div>
          {ret.kind !== 'waiting' && <button className="mk-banner-x" onClick={dismissReturn} aria-label="Dismiss">×</button>}
        </div>
        </div>
      )}

      {loading && (
        <div className="mk-state"><Loader size={16} className="spin" /> Loading…</div>
      )}

      {!loading && !listings.length && (
        // An empty state that says what to do next, rather than announcing that
        // a query returned zero rows.
        <div className="mk-empty">
          <Package size={26} />
          <h2>Nothing listed yet</h2>
          <p>
            The marketplace fills up as builders publish their work. If you have
            models or worlds worth sharing, you can be the first.
          </p>
          <button className="mk-primary" onClick={() => navigate('/creator/listings')}>
            List something
          </button>
        </div>
      )}

      {!loading && listings.length > 0 && (
        <div className="mk-grid">
          {listings.map((l, i) => {
            const isOwned = owned.has(l.id);
            return (
              <article key={l.id} className={`mk-card ${i === 0 && listings.length >= 5 ? 'mk-card-wide' : ''}`}>
                <button type="button" className="mk-thumb mk-thumb-btn" onClick={() => setOpenItem(l.id)} aria-label={`Details: ${l.title}`}>
                  {l.thumbnail_url
                    ? <img src={l.thumbnail_url} alt="" loading="lazy" />
                    : <Package size={22} />}
                  <span className="mk-kind">{l.kind}</span>
                </button>
                <div className="mk-body">
                  <h3><button type="button" className="mk-title-btn" onClick={() => setOpenItem(l.id)}>{l.title}</button></h3>
                  {l.description && <p>{l.description}</p>}
                  {l.kind === 'world' && (
                    <button type="button" className="mk-try" onClick={() => tryIt(l)}>
                      <Eye size={13} /> Try it in 3D
                    </button>
                  )}
                  <div className="mk-foot">
                    <span className="mk-price">{money(l.price_cents, l.currency)}</span>
                    {isOwned ? (
                      <span className="mk-owned">Owned</span>
                    ) : !MARKETPLACE_PURCHASES_ENABLED ? (
                      // Checkout isn't live yet. Keep the button's footprint so
                      // the card reads as "buyable soon", not broken.
                      <button
                        type="button"
                        className="mk-buy mk-buy-soon"
                        disabled
                        aria-disabled="true"
                        title={MARKETPLACE_COMING_SOON_MESSAGE}
                      >
                        <Clock size={14} /> Coming soon
                      </button>
                    ) : l.purchasable === false ? (
                      // Creator hasn't connected payouts yet. The listing is
                      // browsable — hiding it punishes the creator — but a Buy
                      // button that 409s at checkout punishes the buyer.
                      <span className="mk-owned" title="The creator hasn't finished payout setup yet.">
                        Coming soon
                      </span>
                    ) : (
                      <button
                        className="mk-buy"
                        onClick={() => buy(l)}
                        disabled={buying === l.id}
                      >
                        {buying === l.id
                          ? <Loader size={14} className="spin" />
                          : <ShoppingBag size={14} />}
                        {buying === l.id ? 'Opening…' : 'Buy'}
                      </button>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {error && <p className="mk-error">{error}</p>}

      {openItem && (
        <ListingSheet
          listing={openItem}
          creatorName={creators[openItem.creator_id]}
          owned={owned.has(openItem.id)}
          mine={me === openItem.creator_id}
          purchasesOpen={MARKETPLACE_PURCHASES_ENABLED}
          buying={buying === openItem.id}
          onBuy={() => { const l = openItem; setOpenItem(null); buy(l); }}
          onTry={() => tryIt(openItem)}
          onClose={() => setOpenItem(null)}
        />
      )}

      {paying && (
        <CheckoutModal
          custom={{
            title: `${paying.title} · ${money(paying.price_cents, paying.currency)}`,
            blurb: paying.sale_type === 'original'
              ? 'The original: it moves to you and leaves the seller. Held for you for the next few minutes.'
              : 'A copy goes to your library the moment payment clears.',
            start: startListingCheckout(paying),
            onComplete: listingPaid(paying),
          }}
          onClose={() => setPaying(null)}
        />
      )}
    </div>
  );
}
